import { $q, StateService } from '@uirouter/core';
import * as angular from 'angular';
import * as moment from 'moment';
import { NotificationService } from '../../../../../app.common/modules/common/services/notification.service';
import { AccountService } from '../../../account/account.service';
import {
    IExcludedCategory,
    IVoucherBase,
    IVoucherEdit,
    IVoucherItem,
    VoucherStatusEnum,
    VoucherTypesEnum
} from '../../models/models';
import { VouchersService } from '../../vouchers.service';

class AddOrEditVoucherController implements ng.IController {
  public static $inject = [
    'notificationService',
    'accountService',
    'vouchersService',
    '$scope',
    '$state',
    '$uibModal'
  ];
  public waiting: boolean;
  public currencyCssClass: string;
  public currencySign: string;
  public editMode: boolean;
  public voucherTypes: VoucherTypesEnum[] = [];
  public voucherTypesEnum = VoucherTypesEnum;
  public startDateOptions = {
    formatYear: 'yyyy',
    startingDay: 0,
    minDate: moment(new Date())
      .utc(true)
      .startOf('day')
      .toDate()
  };
  public endDateOptions = {
    formatYear: 'yyyy',
    startingDay: 0,
    minDate: moment(new Date())
      .utc(true)
      .startOf('day')
      .toDate()
  };

  public model: IVoucherBase = {
    VoucherType: null,
    DiscountValue: null,
    MinOrderPrice: null,
    StartDate: moment(new Date()).toDate(),
    EndDate: moment(new Date()).toDate(),
    VoucherCode: null,
    RestrictedCategoriesIDs: []
  };

  public voucherErrors = {
    typeRequired: null,
    discountAmountRequired: null,
    discountPercentRequired: null,
    discountEqualZero: null,
    discountOverLimit: null,
    startDateInPast: null,
    endDateGreaterStartDate: null,
    codeMinLengthError: null,
    codeMaxLengthError: null,
    codeRequired: null,
    startDateRequired: null,
    endDateRequired: null,
    minOrderPriceEqualZero: null,
    minOrderPriceOverLimit: null
  };

  public isPublished: boolean = false;

  private voucherID: number;
  private scopeWatchers = [];
  private categories: IExcludedCategory[] = [];
  private restrictedCategoriesIds: number[] = [];

  constructor(
    private notificationService: NotificationService,
    private accountService: AccountService,
    private vouchersService: VouchersService,
    private $scope: angular.IScope,
    private $state: StateService,
    private $uibModal: ng.ui.bootstrap.IModalService,
  ) {}

  public async $onInit() {
    this.currencySign = this.accountService.userData.currencySign;
    this.currencyCssClass = this.getCurrencyCssClass(this.currencySign);
    this.initVoucherTypesList();
    await this.initCategories();
    this.model.RestrictedCategoriesIDs = [...this.restrictedCategoriesIds];
    const id = parseInt(this.$state.params.id, 10);
    this.editMode = !isNaN(id);
    if (this.editMode) {
      this.voucherID = id;
      const voucher = await this.vouchersService.getVoucher(id);
      this.setVoucher(voucher);
    }
    this.setStartDateWatcher();
    this.setTypeWatcher();
  }

  public $onDestroy() {
    this.scopeWatchers.forEach((watcher) => watcher());
  }

  public validateBeforeSave() {
    if (this.isFormValid()) {
      this.editMode
        ? this.updateVoucher({
            ...this.formVoucherBeforeSave(),
            VoucherID: this.voucherID
          })
        : this.saveVoucher(this.formVoucherBeforeSave());
    }
  }

  public getCurrencyCssClass(sign: string): string {
    switch (sign) {
      case '£':
        return 'withPound';
      case 'kr':
        return 'withKrona';
      case '$':
        return 'withDollar';
      case '€':
        return 'withEuro';
      case 'د.إ':
        return 'withDirham';
      default:
        return '';
    }
  }

  public isFormFilled(): boolean {
    return Boolean(this.model.VoucherType) &&
      Boolean(this.model.StartDate) &&
      Boolean(this.model.EndDate) &&
      Boolean(this.model.VoucherCode) &&
      Boolean(this.model.VoucherType === VoucherTypesEnum.FreeDelivery || Boolean(this.model.DiscountValue));
  }

  public async selectCategories() {
    try {
      const operationResult = await this.$uibModal.open({
        component: 'selectCategory',
        resolve: {
            categories: () => [...this.categories],
            disabledCategoriesIDs: () => [...this.restrictedCategoriesIds],
            categoriesIDs: () => [...this.model.RestrictedCategoriesIDs]
        },
        windowClass: 'popup--redesign modal512',
        backdrop: 'static'
       }).result;
      if (operationResult) {
        this.model.RestrictedCategoriesIDs = [...operationResult];
      }
    } catch (e) {
        return;
    }
  }

  public getExcludedCategoriesNames(): string {
    return this.categories
        .filter((category) => !!this.model.RestrictedCategoriesIDs.find((id) => id === category.ID))
        .map((category) => category.Name)
        .join(', ');
  }

  private formVoucherBeforeSave(): IVoucherBase {
    const formedVoucherData = {
      DiscountValue: this.model.DiscountValue
        ? this.model.VoucherType === this.voucherTypesEnum.Percent
          ? Number(this.model.DiscountValue) / 100
          : Number(this.model.DiscountValue)
        : null,
      MinOrderPrice: Number(this.model.MinOrderPrice),
      StartDate: moment.utc(this.model.StartDate)
        .startOf('day')
        .toDate(),
      EndDate: moment.utc(this.model.EndDate)
        .startOf('day')
        .toDate()
    };

    return { ...this.model, ...formedVoucherData };
  }

  private async saveVoucher(voucher: IVoucherBase) {
    this.waiting = true;
    this.notificationService.showWaitingMessage();
    try {
      await this.vouchersService.createVoucher(voucher);
      this.waiting = false;
      this.notificationService.showSuccessMessage();
      this.$state.go('app.vouchers.list');
    } catch (error) {
      this.notificationService.showErrorMessage(error);
      this.waiting = false;
    }
  }

  private async updateVoucher(voucher: IVoucherEdit) {
    this.waiting = true;
    this.notificationService.showWaitingMessage();
    try {
      await this.vouchersService.updateVoucher(voucher);
      this.waiting = false;
      this.notificationService.showSuccessMessage();
      this.$state.go('app.vouchers.list');
    } catch (error) {
      this.notificationService.showErrorMessage(error);
      this.waiting = false;
    }
  }

  private setStartDateWatcher() {
    const voucherStartDateWatcher = this.$scope.$watch('ctrl.model.StartDate', () => {
      if (!this.model) {
        return;
      }

      this.endDateOptions.minDate = moment(this.model.StartDate)
        .startOf('day')
        .toDate();
    });
    this.scopeWatchers.push(voucherStartDateWatcher);
  }

  private setTypeWatcher() {
    const voucherTypeWatcher = this.$scope.$watch('ctrl.model.VoucherType', (newValue, oldValue) => {
      if (!this.model) {
        return;
      }

      if (oldValue && newValue !== oldValue) {
        this.model.DiscountValue = null;
        this.voucherErrors.discountAmountRequired = null;
        this.voucherErrors.discountPercentRequired = null;
        this.voucherErrors.discountEqualZero = null;
        this.voucherErrors.discountOverLimit = null;
      }
    });
    this.scopeWatchers.push(voucherTypeWatcher);
  }

  private isFormValid(): boolean {
    this.voucherErrors.codeMinLengthError = this.model.VoucherCode && this.model.VoucherCode.length < 3;
    this.voucherErrors.codeMaxLengthError = this.model.VoucherCode && this.model.VoucherCode.length > 20;
    this.voucherErrors.codeRequired = !this.model.VoucherCode;
    this.voucherErrors.startDateRequired = !this.model.StartDate;
    this.voucherErrors.endDateRequired = !this.model.EndDate;
    this.voucherErrors.typeRequired = !this.model.VoucherType;
    this.voucherErrors.minOrderPriceEqualZero =
      this.model.MinOrderPrice && Number(this.model.MinOrderPrice) === 0;
    this.voucherErrors.minOrderPriceOverLimit =
          this.model.MinOrderPrice && Number(this.model.MinOrderPrice) > 999999.99;
    this.voucherErrors.startDateInPast = !this.isPublished && moment(this.model.StartDate).isBefore(this.startDateOptions.minDate);
    this.voucherErrors.endDateGreaterStartDate =
      moment(this.model.StartDate) > moment(this.model.EndDate);
    this.voucherErrors.discountAmountRequired =
      this.discountHasRequiredError(VoucherTypesEnum.FixedAmount);
    this.voucherErrors.discountPercentRequired =
      this.discountHasRequiredError(VoucherTypesEnum.Percent);
    this.voucherErrors.discountEqualZero = this.isDiscountEqualZero();
    this.voucherErrors.discountOverLimit = this.isDiscountOverLimit();

    return (
      Object.keys(this.voucherErrors).every((error) => !this.voucherErrors[error])
    );
  }

  private discountHasRequiredError(type: VoucherTypesEnum): boolean {
    return this.model.VoucherType && this.model.VoucherType === type
      ? !this.model.DiscountValue
      : false;
  }

  private isDiscountEqualZero(): boolean {
    return (
      (this.model.VoucherType === VoucherTypesEnum.FixedAmount || this.model.VoucherType === VoucherTypesEnum.Percent) &&
      this.model.DiscountValue &&
      Number(this.model.DiscountValue) === 0
    );
  }

  private isDiscountOverLimit(): boolean {
    return (
      (this.model.VoucherType === VoucherTypesEnum.FixedAmount && this.model.DiscountValue && Number(this.model.DiscountValue) > 999999.99) ||
      (this.model.VoucherType === VoucherTypesEnum.Percent  && this.model.DiscountValue && Number(this.model.DiscountValue) > 100)
    );
  }

  private setVoucher(voucher: IVoucherItem) {
      this.model = voucher;
      this.model.MinOrderPrice = this.model.MinOrderPrice && this.model.MinOrderPrice > 0 ? this.model.MinOrderPrice : null;
      this.model.EndDate = moment(this.model.EndDate).toDate();
      this.model.StartDate = moment(this.model.StartDate).toDate();
      this.model.DiscountValue = this.getPercentDiscountValue(this.model.VoucherType, this.model.DiscountValue);
      this.isPublished = voucher.Status === VoucherStatusEnum.Published;
      if (!this.model.RestrictedCategoriesIDs || this.model.RestrictedCategoriesIDs.length === 0) {
          this.model.RestrictedCategoriesIDs = [...this.restrictedCategoriesIds];
      }
  }

  private getPercentDiscountValue(type: VoucherTypesEnum, value: number) {
    return type === this.voucherTypesEnum.Percent
      ? Number((value * 100).toFixed())
      : value;
  }

  private initVoucherTypesList() {
    Object.keys(VoucherTypesEnum).filter((key) => {
      if (typeof VoucherTypesEnum[key] === 'number') {
        this.voucherTypes.push(VoucherTypesEnum[key]);
      }
    });
  }

  private async initCategories() {
      const response = await $q.all({
          allCategories: this.vouchersService.getClassificationCategories(),
          restrictedCategories: this.vouchersService.getVoucherRestrictedCategories()
      });
      this.categories = response.allCategories.sort((a, b) => a.Name > b.Name ? 1 : a.Name < b.Name ? -1 : 0);
      this.restrictedCategoriesIds = response.restrictedCategories;
  }
}

angular.module('naut').component('addOrEditVoucher', {
  template: require('./add-or-edit-voucher.component.html'),
  controller: AddOrEditVoucherController,
  controllerAs: 'ctrl',
  bindings: {
    close: '&',
    dismiss: '&',
    resolve: '<'
  }
});



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/app.retailer/modules/vouchers/components/add-or-edit-voucher/add-or-edit-voucher.component.ts